/**
 * Lời gọi API khai báo nhà cung cấp, bộ tương thích và cấp tài khoản kết nối DV001.
 * Khớp `apps/api/src/nha-cung-cap/nha-cung-cap.controller.ts`. Một phần đường dẫn là
 * bổ sung ngoài OpenAPI 1.0.1 nên chưa có trong `@htttn/contracts` — dùng `goi()`.
 */
import { goi, LoiApi } from './goi-chung';

export { LoiApi };

export interface NhaCungCap {
  id: string;
  maNhaCungCap: string;
  ten: string;
  dauMoiLienHe: string | null;
  trangThai: 'active' | 'inactive' | string;
  soBoTuongThich: number;
  soThietBi: number;
  createdAt: string;
}

export interface BoTuongThich {
  id: string;
  nhaCungCapId: string;
  maNhaCungCap: string;
  tenNhaCungCap: string;
  noiDatAdapter: 'tren_he_thong_nguon' | 'phia_nha_cung_cap' | string;
  urlGoc: string;
  phuongThucXacThuc: 'tai_khoan' | 'khoa_api' | string;
  dichVuHoTro: string[];
  danhSachIpChoPhep: string[];
  gioiHanTanSuat: number;
  thoiGianPhanHoiCamKetMs: number | null;
  /** Đường dẫn từng dịch vụ của adapter đã khai (khoá DV002/DV003/DV004/DV009/…). */
  duongDanDichVu: Record<string, string>;
  /** Tài khoản nhà cung cấp cấp để hệ thống nguồn gọi vào adapter (không có mật khẩu). */
  xacThucGoiVao: XacThucGoiVao;
  coMatKhauGoiVao: boolean;
  trangThaiKiemThuTuanThu: 'chua_kiem_thu' | 'dat' | 'khong_dat' | string;
  thoiDiemKiemThuGanNhat: string | null;
  trangThai: 'active' | 'inactive' | string;
  tenDangNhap: string | null;
  biKhoaDen: string | null;
  soLanSai: number;
  createdAt: string;
  /**
   * Service gửi lệnh `dv-ncc-<ma>` của nhà cung cấp (hợp đồng Z-DVNCC), dùng chung cho mọi
   * bộ tương thích của nhà cung cấp; `null`/vắng = chưa có, gửi lệnh trực tiếp tới adapter.
   */
  serviceDvNcc?: ServiceDvNcc | null;
}

/** Tóm tắt service dv-ncc trả kèm danh sách bộ tương thích. */
export interface ServiceDvNcc {
  tenService: string;
  congNoiBo: number;
  trangThaiVanHanh: 'dang_chay' | 'dang_trien_khai' | 'tam_dung' | 'loi' | 'cau_dao_mo' | string;
  trangThai: 'active' | 'inactive' | string;
}

/** Thông tin đăng nhập chỉ trả về một lần sau khi cấp. */
export interface TaiKhoanVuaCap {
  tenDangNhap: string;
  matKhau: string;
  diaChiXacThuc: string;
}

export interface TinhTrangTaiKhoanThietBi {
  deviceId: string;
  cumLoaId: string;
  phuongThucKetNoi: string;
  coTaiKhoan: boolean;
  tenDangNhap: string | null;
  thoiDiemCap: string | null;
  thoiDiemThuHoi: string | null;
  biKhoaDen: string | null;
  soLanSai: number;
  soTokenConHieuLuc: number;
}

export interface ThanNhaCungCap {
  maNhaCungCap?: string;
  ten?: string;
  dauMoiLienHe?: string;
  trangThai?: 'active' | 'inactive';
}

export interface ThanBoTuongThich {
  maNhaCungCap?: string;
  noiDatAdapter?: string;
  urlGoc?: string;
  phuongThucXacThuc?: string;
  dichVuHoTro?: string[];
  danhSachIpChoPhep?: string[];
  gioiHanTanSuat?: number;
  thoiGianPhanHoiCamKetMs?: number | null;
  duongDanDichVu?: Record<string, string>;
  xacThucGoiVao?: (XacThucGoiVao & { matKhau?: string }) | { xoa: true };
  trangThai?: 'active' | 'inactive';
}

export interface XacThucGoiVao {
  urlToken?: string;
  taiKhoan?: string;
  truongTaiKhoan?: string;
  truongMatKhau?: string;
  duongDanToken?: string;
}

/** Kết quả bộ kiểm thử tuân thủ (`POST /vendor-adapters/{id}/conformance-test`). */
export interface KetQuaCaKiemThu {
  ma: string;
  ten: string;
  ketQua: 'dat' | 'khong_dat' | 'bo_qua';
  mongDoi: string;
  thucTe: string;
  thoiGianMs: number;
  batBuoc: boolean;
}

export interface KetQuaTuanThu {
  adapterId: string;
  maNhaCungCap: string;
  urlGoc: string;
  thoiDiem: string;
  soCa: number;
  soDat: number;
  soKhongDat: number;
  ketLuan: 'dat' | 'khong_dat';
  ca: KetQuaCaKiemThu[];
}

export interface KetQuaThuToken {
  thanhCong: boolean;
  tokenRutGon?: string;
  hetHanLuc?: string;
  thoiGianMs: number;
  loi?: string;
}

const id = (s: string): string => encodeURIComponent(s);

export const nhaCungCapApi = {
  danhSach: (signal?: AbortSignal) => goi<NhaCungCap[]>('/api/v1/vendors/details', { signal }),
  tao: (than: ThanNhaCungCap) => goi<NhaCungCap>('/api/v1/vendors', { phuongThuc: 'POST', than }),
  sua: (ma: string, than: ThanNhaCungCap) =>
    goi<NhaCungCap>(`/api/v1/vendors/${id(ma)}`, { phuongThuc: 'PATCH', than }),

  danhSachBoTuongThich: (signal?: AbortSignal) =>
    goi<BoTuongThich[]>('/api/v1/vendor-adapters/details', { signal }),
  taoBoTuongThich: (than: ThanBoTuongThich) =>
    goi<{ boTuongThich: BoTuongThich; taiKhoan: TaiKhoanVuaCap }>('/api/v1/vendor-adapters', {
      phuongThuc: 'POST',
      than,
    }),
  suaBoTuongThich: (ma: string, than: ThanBoTuongThich) =>
    goi<BoTuongThich>(`/api/v1/vendor-adapters/${id(ma)}`, { phuongThuc: 'PATCH', than }),
  capLaiBoTuongThich: (ma: string) =>
    goi<TaiKhoanVuaCap>(`/api/v1/vendor-adapters/${id(ma)}/credentials`, { phuongThuc: 'POST' }),
  thuTokenBoTuongThich: (ma: string) =>
    goi<KetQuaThuToken>(`/api/v1/vendor-adapters/${id(ma)}/test-token`, { phuongThuc: 'POST' }),
  kiemThuTuanThu: (ma: string) =>
    goi<KetQuaTuanThu>(`/api/v1/vendor-adapters/${id(ma)}/conformance-test`, { phuongThuc: 'POST' }),
  xoaBoTuongThich: (ma: string) =>
    goi<{ id: string; daXoa: true; serviceDvNcc: 'giu' | 'chuyen' | 'go' | 'khong_co' }>(
      `/api/v1/vendor-adapters/${id(ma)}`,
      { phuongThuc: 'DELETE' },
    ),
  moKhoaBoTuongThich: (ma: string) =>
    goi<BoTuongThich>(`/api/v1/vendor-adapters/${id(ma)}/unlock`, { phuongThuc: 'POST' }),

  tinhTrangThietBi: (ma: string, signal?: AbortSignal) =>
    goi<TinhTrangTaiKhoanThietBi>(`/api/v1/devices/${id(ma)}/credentials`, { signal }),
  capThietBi: (ma: string) =>
    goi<TaiKhoanVuaCap & { tinhTrang: TinhTrangTaiKhoanThietBi }>(`/api/v1/devices/${id(ma)}/credentials`, {
      phuongThuc: 'POST',
    }),
  thuHoiThietBi: (ma: string, lyDo: string) =>
    goi<TinhTrangTaiKhoanThietBi>(`/api/v1/devices/${id(ma)}/tokens/revoke`, {
      phuongThuc: 'POST',
      than: { lyDo },
    }),
  kichHoatLaiThietBi: (ma: string) =>
    goi<TinhTrangTaiKhoanThietBi>(`/api/v1/devices/${id(ma)}/credentials/reactivate`, { phuongThuc: 'POST' }),
  moKhoaThietBi: (ma: string) =>
    goi<TinhTrangTaiKhoanThietBi>(`/api/v1/devices/${id(ma)}/credentials/unlock`, { phuongThuc: 'POST' }),
};

export type NhaCungCapApi = typeof nhaCungCapApi;
